/**
 * Phase 2 domain types shared between the API and React client.
 *
 * Money fields are decimal strings (e.g. "1200000.00") matching the wire format.
 */

import type { ContractSignatureEvidence } from './private-documents';

export const CUSTOMER_PROFILE_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;
export type CustomerProfileStatus = (typeof CUSTOMER_PROFILE_STATUSES)[number];

export const ONBOARDING_STAGES = [
  'REGISTERED',
  'PROFILE_INCOMPLETE',
  'PROFILE_COMPLETE',
  'CONSENTS_PENDING',
  'ONBOARDING_COMPLETE',
] as const;
export type OnboardingStage = (typeof ONBOARDING_STAGES)[number];

export const ADDRESS_TYPES = ['RESIDENTIAL', 'INSTALLATION', 'BILLING', 'CORRESPONDENCE'] as const;
export type AddressType = (typeof ADDRESS_TYPES)[number];

export const NIGERIAN_STATE_OPTIONS = [
  { value: 'ABIA', label: 'Abia' },
  { value: 'ADAMAWA', label: 'Adamawa' },
  { value: 'AKWA_IBOM', label: 'Akwa Ibom' },
  { value: 'ANAMBRA', label: 'Anambra' },
  { value: 'BAUCHI', label: 'Bauchi' },
  { value: 'BAYELSA', label: 'Bayelsa' },
  { value: 'BENUE', label: 'Benue' },
  { value: 'BORNO', label: 'Borno' },
  { value: 'CROSS_RIVER', label: 'Cross River' },
  { value: 'DELTA', label: 'Delta' },
  { value: 'EBONYI', label: 'Ebonyi' },
  { value: 'EDO', label: 'Edo' },
  { value: 'EKITI', label: 'Ekiti' },
  { value: 'ENUGU', label: 'Enugu' },
  { value: 'FCT', label: 'Federal Capital Territory' },
  { value: 'GOMBE', label: 'Gombe' },
  { value: 'IMO', label: 'Imo' },
  { value: 'JIGAWA', label: 'Jigawa' },
  { value: 'KADUNA', label: 'Kaduna' },
  { value: 'KANO', label: 'Kano' },
  { value: 'KATSINA', label: 'Katsina' },
  { value: 'KEBBI', label: 'Kebbi' },
  { value: 'KOGI', label: 'Kogi' },
  { value: 'KWARA', label: 'Kwara' },
  { value: 'LAGOS', label: 'Lagos' },
  { value: 'NASARAWA', label: 'Nasarawa' },
  { value: 'NIGER', label: 'Niger' },
  { value: 'OGUN', label: 'Ogun' },
  { value: 'ONDO', label: 'Ondo' },
  { value: 'OSUN', label: 'Osun' },
  { value: 'OYO', label: 'Oyo' },
  { value: 'PLATEAU', label: 'Plateau' },
  { value: 'RIVERS', label: 'Rivers' },
  { value: 'SOKOTO', label: 'Sokoto' },
  { value: 'TARABA', label: 'Taraba' },
  { value: 'YOBE', label: 'Yobe' },
  { value: 'ZAMFARA', label: 'Zamfara' },
] as const;

export const NIGERIAN_STATE_CODES = NIGERIAN_STATE_OPTIONS.map((state) => state.value);
export type NigerianStateCode = (typeof NIGERIAN_STATE_OPTIONS)[number]['value'];

export const EMPLOYMENT_STATUSES = [
  'FULL_TIME_EMPLOYED',
  'PART_TIME_EMPLOYED',
  'SELF_EMPLOYED',
  'OTHER',
] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

export const CONSENT_TYPES = [
  'PLATFORM_TERMS',
  'PRIVACY_NOTICE',
  'REPAYMENT_AGREEMENT',
  'DIRECT_DEBIT_PREPARATION',
  'ELECTRONIC_COMMUNICATION',
] as const;
export type ConsentType = (typeof CONSENT_TYPES)[number];

export const PRODUCT_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const PLAN_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
export type PlanStatus = (typeof PLAN_STATUSES)[number];

export const DEPOSIT_TYPES = ['FIXED', 'PERCENTAGE'] as const;
export type DepositType = (typeof DEPOSIT_TYPES)[number];

export const REPAYMENT_FREQUENCIES = ['MONTHLY'] as const;
export type RepaymentFrequency = (typeof REPAYMENT_FREQUENCIES)[number];

export const SUBSCRIPTION_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'CHANGES_REQUESTED',
  'APPROVED',
  'REJECTED',
  'AWAITING_CUSTOMER_ACCEPTANCE',
  'ACCEPTED',
  'AWAITING_MANDATE',
  'MANDATE_ACTIVE',
  'ACTIVE',
  'SUSPENDED',
  'COMPLETED',
  'CANCELLED',
  'DEFAULTED',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const APPLICATION_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'CHANGES_REQUESTED',
  'REJECTED',
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const CONTRACT_STATUSES = [
  'AWAITING_CUSTOMER_ACCEPTANCE',
  'ACCEPTED',
  'AWAITING_MANDATE',
  'MANDATE_ACTIVE',
  'ACTIVE',
  'SUSPENDED',
  'COMPLETED',
  'CANCELLED',
  'DEFAULTED',
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const SCHEDULE_STATUSES = [
  'SCHEDULED',
  'DUE',
  'PAID',
  'PARTIALLY_PAID',
  'OVERDUE',
  'WAIVED',
  'CANCELLED',
] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUSES)[number];

export interface CustomerProfile {
  id: string;
  userId: string;
  customerNumber: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  phoneNumber: string;
  alternatePhone: string | null;
  dateOfBirth: string | null;
  occupation: string | null;
  employerName: string | null;
  status: CustomerProfileStatus;
  onboardingStage: OnboardingStage;
  onboardingCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerAddress {
  id: string;
  customerId: string;
  addressType: AddressType;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  localGovernmentArea: string | null;
  stateCode: string;
  countryCode: string;
  postalCode: string | null;
  isDefault: string;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerConsent {
  id: string;
  customerId: string;
  consentType: ConsentType;
  documentVersion: string;
  acceptedAt: string;
  actorUserId: string;
  withdrawnAt: string | null;
  createdAt: string;
}

export interface ProductComponent {
  id: string;
  productId: string;
  componentType: string;
  componentName: string;
  description: string | null;
  quantity: number;
  unit: string;
  modelSpecification: string | null;
  warrantyMonths: number | null;
  displayOrder: number;
  isRequired: string;
  createdAt: string;
  updatedAt: string;
}

export interface SolarProduct {
  id: string;
  code: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  systemCapacityKw: string;
  basePrice: string;
  currency: string;
  warrantySummary: string | null;
  status: ProductStatus;
  isAvailable: string;
  createdAt: string;
  updatedAt: string;
  components?: ProductComponent[];
}

export interface RepaymentPlanTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  minProductPrice: string | null;
  maxProductPrice: string | null;
  depositType: DepositType;
  depositValue: string;
  tenureMonths: number;
  repaymentFrequency: RepaymentFrequency;
  calculationMethod: string;
  status: PlanStatus;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionApplication {
  id: string;
  contractReference: string | null;
  customerId: string;
  productId: string;
  planTemplateId: string;
  installationAddressId: string | null;
  employmentStatus: EmploymentStatus | null;
  status: SubscriptionStatus;
  version: number;
  productCodeSnapshot: string | null;
  productNameSnapshot: string | null;
  componentSnapshot: unknown;
  productPrice: string | null;
  discountAmount: string;
  discountProvenance: 'NONE' | 'STAFF_AUTHORIZED' | 'LEGACY_UNVERIFIED';
  depositAmount: string | null;
  financedAmount: string | null;
  totalRepayableAmount: string | null;
  tenureMonths: number | null;
  repaymentFrequency: string | null;
  instalmentAmount: string | null;
  finalInstalmentAmount: string | null;
  currency: string;
  preferredDebitDay: number | null;
  plannedFirstDueDate: string | null;
  repaymentScheduleModel?: string | null;
  approvedFirstRepaymentDate?: string | null;
  approvedRepaymentSchedule?: readonly ApprovedRepaymentInstalment[] | null;
  offerExpired?: boolean;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  changeRequestReason: string | null;
  preparedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  requestedTerms: RequestedCommercialTerms | null;
  offerPartyPreview: ContractPartySnapshot | null;
  acceptanceSnapshot: ContractAcceptanceSnapshot | null;
  offerAmendments: readonly OfferAmendmentView[];
}

export interface ContractOfferDocument {
  offerVersion?: number;
  templateVersion: string;
  canonicalContent: string;
  contentSha256: string;
}

/** Immutable server-calculated commercial terms captured at submission time. */
export interface RequestedCommercialTerms {
  id: string;
  subscriptionId: string;
  version: number;
  productId: string;
  productCode: string;
  productName: string;
  productDescription: string | null;
  productWarrantySummary: string | null;
  componentSnapshot: unknown;
  productBasePrice: string;
  planTemplateId: string;
  planCode: string;
  planName: string;
  planEffectiveFrom: string | null;
  planEffectiveTo: string | null;
  depositType: DepositType;
  depositValue: string;
  calculationMethod: string;
  repaymentFrequency: RepaymentFrequency;
  tenureMonths: number;
  discountAmount: string;
  discountProvenance: 'NONE' | 'STAFF_AUTHORIZED' | 'LEGACY_UNVERIFIED';
  depositAmount: string;
  financedAmount: string;
  instalmentAmount: string;
  finalInstalmentAmount: string;
  totalRepayableAmount: string;
  currency: string;
  preferredDebitDay: number | null;
  requestedFirstDueDate: string | null;
  employmentStatus: EmploymentStatus | null;
  installationAddressId: string | null;
  installationAddressSnapshot: ContractAddressSnapshot | null;
  capturedAt: string;
}

export interface ContractAddressSnapshot {
  id: string;
  addressType: AddressType;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  localGovernmentArea: string | null;
  stateCode: string;
  countryCode: string;
  postalCode: string | null;
}

export interface ContractPartySnapshot {
  customerId: string;
  customerNumber: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  legalName: string;
  verifiedEmail: string;
  phoneNumber: string;
  residentialAddress: ContractAddressSnapshot;
  installationAddress: ContractAddressSnapshot;
}

export interface ContractCommercialSnapshot {
  subscriptionId: string;
  productId: string;
  productCode: string;
  productName: string;
  productDescription: string | null;
  productWarrantySummary: string | null;
  componentSnapshot: unknown;
  planTemplateId: string;
  planCode: string;
  planName: string;
  planEffectiveFrom: string | null;
  planEffectiveTo: string | null;
  productPrice: string;
  depositAmount: string;
  financedAmount: string;
  discountAmount: string;
  discountProvenance: 'NONE' | 'STAFF_AUTHORIZED' | 'LEGACY_UNVERIFIED';
  instalmentAmount: string;
  finalInstalmentAmount: string;
  totalRepayableAmount: string;
  tenureMonths: number;
  repaymentFrequency: RepaymentFrequency;
  preferredDebitDay: number | null;
  expectedFirstDueDate: string;
  approvedFirstRepaymentDate?: string;
  repaymentSchedule?: readonly ApprovedRepaymentInstalment[];
  scheduleSource?: 'ADMIN_APPROVED_DATE_V1';
  currency: string;
}

/** Immutable commercial rows; live payment status is deliberately separate. */
export interface ApprovedRepaymentInstalment {
  sequenceNumber: number;
  dueDate: string;
  openingBalance: string;
  instalmentAmount: string;
  principalComponent: string;
  feeComponent: string;
  closingBalance: string;
}

export interface ContractAcceptanceSnapshot {
  id: string;
  subscriptionId: string;
  requestedTermsId: string;
  requestedTermsVersion: number;
  templateVersion: string;
  canonicalContent: string;
  contentSha256: string;
  party: ContractPartySnapshot;
  commercial: ContractCommercialSnapshot;
  acceptedAt: string;
  acceptedByUserId: string;
  signature: ContractSignatureEvidence | null;
  evidence: {
    ipAddressRecorded: boolean;
    userAgentRecorded: boolean;
    correlationId: string | null;
  };
}

export interface OfferAmendmentView {
  id: string;
  requestedTermsId: string;
  requestedTermsVersion: number;
  changedFields: readonly string[];
  requestedValues: Readonly<Record<string, unknown>>;
  approvedValues: Readonly<Record<string, unknown>>;
  reason: string;
  approvedByUserId: string;
  createdAt: string;
}

export interface RepaymentScheduleEntry {
  id: string;
  subscriptionId: string;
  sequenceNumber: number;
  dueDate: string;
  openingBalance: string;
  instalmentAmount: string;
  principalComponent: string;
  feeComponent: string;
  closingBalance: string;
  status: ScheduleStatus;
  generatedVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountMasked {
  id: string;
  customerId: string;
  bankCode: string;
  bankNameSnapshot: string;
  accountName: string;
  accountNumberMasked: string;
  isDefault: string;
  status: string;
  verificationStatus: string;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommercialSummary {
  productPrice: string;
  discountAmount: string;
  depositAmount: string;
  financedAmount: string;
  totalRepayableAmount: string;
  tenureMonths: number;
  instalmentAmount: string;
  finalInstalmentAmount: string;
}

export interface CommercialSummaryInput {
  productPrice: string;
  discountAmount?: string;
  depositType: DepositType;
  depositValue: string;
  tenureMonths: number;
}

/** Statuses shown on the customer applications list. */
export function isApplicationStatus(status: string): boolean {
  return (APPLICATION_STATUSES as readonly string[]).includes(status);
}

/** Statuses shown on the customer contracts list. */
export function isContractStatus(status: string): boolean {
  return (CONTRACT_STATUSES as readonly string[]).includes(status);
}
